import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faExternalLinkAlt,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportDepoOrBatchProcessedRows,
  getDepoOrBatchProcessedRows,
  getDepoOrBatchUploadHistory,
} from "../../services/settlementApi";

const DepoOrBatchUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [filterPrn, setFilterPrn] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterOriginalFile, setFilterOriginalFile] = useState("");
  const [filterGeneratedFile, setFilterGeneratedFile] = useState("");
  const [filterResult, setFilterResult] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  const hasFilters = useMemo(
    () => !!(filterPrn || filterType || filterOriginalFile || filterGeneratedFile || filterResult || filterDetails),
    [filterPrn, filterType, filterOriginalFile, filterGeneratedFile, filterResult, filterDetails]
  );

  const fetchUploadInfo = useCallback(async () => {
    if (!uploadId) return;

    setLoadingInfo(true);
    try {
      const response = await getDepoOrBatchUploadHistory({ page: 1, limit: 2000 });
      const found = (response.data?.data || []).find((item) => item.upload_id === uploadId);
      if (!found) {
        toast.error("Upload not found");
        navigate("/depo-or-batch-upload-history");
        return;
      }
      setUploadInfo(found);
    } catch (error) {
      toast.error("Failed to load upload info");
      console.error(error);
    } finally {
      setLoadingInfo(false);
    }
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;

    setLoadingRows(true);
    try {
      const response = await getDepoOrBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      setRows(response.data?.data || []);
      setPage(1);
    } catch (error) {
      toast.error("Failed to load processed rows");
      console.error(error);
    } finally {
      setLoadingRows(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchUploadInfo();
  }, [fetchUploadInfo]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const includes = (value, filter) => !filter || String(value || "").toLowerCase().includes(filter.toLowerCase());

    return rows.filter((row) => {
      if (!includes(row.payment_ref_no, filterPrn)) return false;
      if (!includes(row.upload_type, filterType)) return false;
      if (!includes(row.original_file_name, filterOriginalFile)) return false;
      if (!includes(row.generated_file_name, filterGeneratedFile)) return false;
      if (!includes(row.result_type, filterResult)) return false;

      const details = row.error_message || row.processed_by || "";
      if (!includes(details, filterDetails)) return false;

      return true;
    });
  }, [rows, filterPrn, filterType, filterOriginalFile, filterGeneratedFile, filterResult, filterDetails]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));

  const handleClearFilters = () => {
    setFilterPrn("");
    setFilterType("");
    setFilterOriginalFile("");
    setFilterGeneratedFile("");
    setFilterResult("");
    setFilterDetails("");
    setPage(1);
  };

  const handleExport = async (type) => {
    if (!uploadId) return;

    setExporting(true);
    try {
      const response = await exportDepoOrBatchProcessedRows(uploadId, { type });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `depo_or_batch_${uploadId}_${type}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${type} rows`);
    } catch (error) {
      toast.error("Failed to export rows");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const resultBadge = (resultType) =>
    resultType === "success" ? (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>
    ) : (
      <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>
    );

  if (loadingInfo && !uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12 text-gray-600">Loading upload information...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate("/depo-or-batch-upload-history")}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Deposit Slip / OR Upload Processed</h1>
          <p className="text-gray-600 text-sm mt-0.5 font-mono">{uploadId}</p>
        </div>
      </div>

      {uploadInfo && (
        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Files</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p className="text-lg font-bold text-gray-700 capitalize">{uploadInfo.status || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                {uploadInfo.uploaded_by_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-xs text-gray-700">{formatDateTime(uploadInfo.created_at)}</p>
            </div>
          </div>
          {uploadInfo.error_message && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {uploadInfo.error_message}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "all", label: `All (${uploadInfo?.total_rows || 0})` },
              { key: "success", label: `Success (${uploadInfo?.success_count || 0})` },
              { key: "failed", label: `Failed (${uploadInfo?.failed_count || 0})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setRowType(key)}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  rowType === key
                    ? key === "success"
                      ? "bg-green-600 text-white"
                      : key === "failed"
                      ? "bg-red-600 text-white"
                      : "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={fetchRows}
              disabled={loadingRows}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faRefresh} className={loadingRows ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleExport("all")}
              disabled={exporting || (uploadInfo?.total_rows || 0) === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export All
            </button>
            <button
              onClick={() => handleExport("failed")}
              disabled={exporting || (uploadInfo?.failed_count || 0) === 0}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export Failed
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
            Filter Rows (frontend)
          </p>
          {hasFilters && (
            <button onClick={handleClearFilters} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1">
              <FontAwesomeIcon icon={faTimes} />
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <input
            type="text"
            value={filterPrn}
            onChange={(e) => {
              setFilterPrn(e.target.value);
              setPage(1);
            }}
            placeholder="Payment Ref No"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          />
          <input
            type="text"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
            placeholder="Type (DS/OR)"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          />
          <input
            type="text"
            value={filterOriginalFile}
            onChange={(e) => {
              setFilterOriginalFile(e.target.value);
              setPage(1);
            }}
            placeholder="Original file name"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          />
          <input
            type="text"
            value={filterGeneratedFile}
            onChange={(e) => {
              setFilterGeneratedFile(e.target.value);
              setPage(1);
            }}
            placeholder="Generated file name"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          />
          <select
            value={filterResult}
            onChange={(e) => {
              setFilterResult(e.target.value);
              setPage(1);
            }}
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          >
            <option value="">All Results</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <input
            type="text"
            value={filterDetails}
            onChange={(e) => {
              setFilterDetails(e.target.value);
              setPage(1);
            }}
            placeholder="Error / Processed by"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs lg:text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Row #</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Payment Ref No</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Original File</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Generated File</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Result</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Details</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingRows ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-gray-500">No rows found</td>
                </tr>
              ) : (
                paginatedRows.map((row, idx) => (
                  <tr key={`${row.id}-${idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                    <td className="px-3 py-2.5 text-gray-800">{row.payment_ref_no || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-800">{row.upload_type || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-800 max-w-xs truncate" title={row.original_file_name || ""}>
                      {row.original_file_name || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-gray-800 max-w-xs truncate" title={row.generated_file_name || ""}>
                      {row.generated_file_name || "—"}
                    </td>
                    <td className="px-3 py-2.5">{resultBadge(row.result_type)}</td>
                    <td className="px-3 py-2.5 text-xs max-w-xs">
                      {row.error_message ? (
                        <div className="text-red-700 truncate" title={row.error_message}>
                          {row.error_message}
                        </div>
                      ) : (
                        <div className="text-gray-600" title={row.processed_by || ""}>
                          {row.processed_by ? `Processed by ${row.processed_by}` : "Processed"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.result_type === "success" && row.public_share_url ? (
                        <button
                          onClick={() => window.open(row.public_share_url, "_blank", "noopener,noreferrer")}
                          className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                          Open File
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <span className="text-sm font-medium text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DepoOrBatchUploadProcessedPage;
